import { shallowEqual } from 'react-redux'
import { createSelector, lruMemoize } from 'reselect'
import { IWaterSystem } from '../../types'
import { WaterSystemCoordinatesSelector } from '../publicWaterSystem'
import { RootState } from '../store'
import { WaterSystemType } from './waterSystems.slice'

interface WaterSystemsIdsAndAddresses {
  id: string
  address: string
}

export interface FormattedWaterSystem {
  pwsName: string
  address: string
  city: string
  connections: number
  population: number
  violations: number
}

const loading = (state: RootState) => state.waterSystems.loading
const error = (state: RootState) => state.waterSystems.error
const success = (state: RootState) => state.waterSystems.success
const getUserSearched = (state: RootState) => state.waterSystems.userSearched
const getWaterSystems = (state: RootState) => state.waterSystems.data.waterSystems || []
const getSelectedWaterSystem = (state: RootState) => state.waterSystems.selectedWaterSystem

const getSelectedWaterSystemType = (state: RootState) =>
  state.waterSystems.selectedSystemType as WaterSystemType

const makeSelectedWaterSystem = createSelector(
  getSelectedWaterSystem,
  WaterSystemCoordinatesSelector.getWaterSystemCoordinates, //just getting data from another part of the store if not available
  getWaterSystems,
  (pwsid: string, coordSystems: IWaterSystem[], waterSystems: IWaterSystem[]): IWaterSystem => {
    return (
      [...waterSystems, ...coordSystems].find((item) => item.PWSID === pwsid) ||
      ({} as IWaterSystem)
    )
  },
  {
    memoize: lruMemoize,
    memoizeOptions: {
      equalityCheck: shallowEqual,
      resultEqualityCheck: shallowEqual,
      maxSize: 10,
    },
    argsMemoize: lruMemoize,
    argsMemoizeOptions: {
      equalityCheck: shallowEqual,
      resultEqualityCheck: shallowEqual,
      maxSize: 10,
    },
  },
)
const makeWaterSystems = createSelector([getWaterSystems], (data) => {
  if (!data.length) return []

  return data.map((system) => ({
    ...system,
    totalPopulation: system.POPULATION_SERVED_COUNT,
    totalConnections: system.SERVICE_CONNECTIONS_COUNT,
    totalViolations: system.VIOLATIONS_COUNT,
  }))
})

const makeWaterSystemsSort = createSelector(
  [makeWaterSystems, (_, inputZipCode) => inputZipCode],
  (data, inputZipCode) => {
    return [...data].sort((a, b) => {
      const aMatches = a.ZIP_CODE === inputZipCode
      const bMatches = b.ZIP_CODE === inputZipCode

      if (aMatches && !bMatches) return -1
      if (!aMatches && bMatches) return 1

      if (aMatches && bMatches) {
        return a.PWSID.localeCompare(b.PWSID)
      }

      const zipComparison = a.ZIP_CODE!.localeCompare(b.ZIP_CODE!)
      if (zipComparison === 0) {
        return a.PWSID.localeCompare(b.PWSID)
      }
      return zipComparison
    })
  },
)

export const selectWaterSystemsIdsAndAddresses = createSelector(
  [getWaterSystems],
  (data: IWaterSystem[]): WaterSystemsIdsAndAddresses[] => {
    if (!data.length) return []

    return data.map((system: IWaterSystem) => ({
      id: system._id,
      address: system.ADDRESS_LINE1 || '',
    }))
  },
)
const makeSelectedWaterSystemFormatted = createSelector(
  makeSelectedWaterSystem,
  (system: IWaterSystem): FormattedWaterSystem => {
    return {
      pwsName: (system && system.PWS_NAME) || 'PWS Name not available',
      address:
        (system && system.ADDRESS_LINE1) ||
        (system && system.ADDRESS_LINE2) ||
        'Address not available',
      city: (system && system.CITY_NAME) || 'City not available',
      connections: (system && system.SERVICE_CONNECTIONS_COUNT) || 0,
      population: (system && system.POPULATION_SERVED_COUNT) || 0,
      violations: (system && system.VIOLATIONS_COUNT) || 0,
    }
  },
  {
    memoize: lruMemoize,
    memoizeOptions: {
      equalityCheck: shallowEqual,
      resultEqualityCheck: shallowEqual,
      maxSize: 10,
    },
    argsMemoize: lruMemoize,
    argsMemoizeOptions: {
      equalityCheck: shallowEqual,
      resultEqualityCheck: shallowEqual,
      maxSize: 10,
    },
  },
)

export const WaterSystemsSelector = {
  error,
  getSelectedWaterSystem,
  getSelectedWaterSystemType,
  getUserSearched,
  getWaterSystems,
  loading,
  makeSelectedWaterSystem,
  makeSelectedWaterSystemFormatted,
  makeWaterSystems,
  makeWaterSystemsSort,
  selectWaterSystemsIdsAndAddresses,
  success,
}
